import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import NumericInput from "@/components/NumericInput";
import { Percent, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Perakende Fiyatlandırma (FAZ 1) — tenant varsayılan artış oranı ayarı.
export default function PricingTab({ canEdit = false }) {
  const [rate, setRate] = useState(50);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products/pricing-settings");
      setRate(Number(data?.default_markup_percent ?? 50));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      await api.put("/products/pricing-settings", { default_markup_percent: Number(rate) || 0 });
      toast.success("Varsayılan perakende artış oranı kaydedildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const example = (1000 * (100 + (Number(rate) || 0))) / 100;

  return (
    <div className="space-y-4" data-testid="pricing-tab">
      <Card className="bg-slate-950/60 border-slate-800 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
          <Percent className="w-4 h-4" /> Perakende Fiyatlandırma — şirket genelinde varsayılan artış oranı.
        </div>
        <div className="max-w-xs">
          <Label className="text-xs">Varsayılan Perakende Fiyat Artış Oranı (%)</Label>
          <NumericInput
            data-testid="pricing-default-markup-input"
            step="0.1"
            min={0}
            value={rate}
            onChange={(v) => setRate(v)}
            disabled={!canEdit}
            className="bg-slate-950 border-slate-700 rounded-sm mt-1 font-mono"
          />
          <div className="text-[11px] text-slate-500 mt-2" data-testid="pricing-example">
            Yeni ürünlerde önerilen perakende satış fiyatı = Alış Fiyatı × (1 + Oran / 100).
            Örnek: alış ₺1.000,00, oran %{Number(rate) || 0} → ₺{example.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        {canEdit && (
          <Button
            data-testid="pricing-save-btn"
            onClick={save}
            disabled={saving || loading}
            className="mt-4 bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Kaydet</>}
          </Button>
        )}
      </Card>
      <DiscountLimitCard canEdit={canEdit} />
    </div>
  );
}

// Satış İskonto Onay Limiti (tenant bazlı) — backend system_settings REUSE.
function DiscountLimitCard({ canEdit = false }) {
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/discount-limit");
      setLimit(Number(data?.approval_limit_percent ?? 10));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      await api.put("/settings/discount-limit", { approval_limit_percent: Number(limit) });
      toast.success("İskonto onay limiti kaydedildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-slate-950/60 border-slate-800 p-4" data-testid="discount-limit-card">
      <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
        <Percent className="w-4 h-4" /> Satış İskonto Onayı — şirket genelinde limit.
      </div>
      <div className="max-w-xs">
        <Label className="text-xs">Yönetici Onayı Gerektiren İskonto Limiti (%)</Label>
        <NumericInput
          data-testid="discount-limit-input"
          step="0.1"
          min={0}
          max={100}
          value={limit}
          onChange={(v) => setLimit(v)}
          disabled={!canEdit}
          className="bg-slate-950 border-slate-700 rounded-sm mt-1 font-mono"
        />
        <div className="text-[11px] text-slate-500 mt-2" data-testid="discount-limit-hint">
          Satır iskontosu %{Number(limit) ?? 0} ve altı → normal akış. Üzeri → teklif "yönetici onayı bekliyor" olarak kaydedilir.
        </div>
      </div>
      {canEdit && (
        <Button
          data-testid="discount-limit-save-btn"
          onClick={save}
          disabled={saving || loading}
          className="mt-4 bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Kaydet</>}
        </Button>
      )}
    </Card>
  );
}
